import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, X, Minus, MessageSquare, Flag } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { api, cachedGet, errMsg, queue, setCache } from "@/lib/api";
import { Btn, Loading, StatusPill, useStageNames, fmtDateTime } from "@/components/app/bits";
import { SignaturePad } from "@/components/app/SignaturePad";
import { PhotoStrip, PunchDialog } from "@/components/app/PunchDialog";

const RESULTS = [["pass", "Pass", Check, "bg-emerald-500 text-[#080D1A] border-emerald-500"], ["fail", "Fail", X, "bg-red-500 text-white border-red-500"], ["na", "N/A", Minus, "bg-slate-500 text-white border-slate-500"]];

function ItemRow({ item, idx, readOnly, onChange, onPunch }) {
  const [showComment, setShowComment] = useState(!!item.comment);
  return (
    <div className="py-4 border-b border-[#1E2D4A] last:border-0" data-testid={`check-item-${idx}`}>
      <p className="text-slate-100 mb-3"><span className="num text-slate-500 mr-2">{idx + 1}.</span>{item.text}</p>
      <div className="flex flex-wrap items-center gap-2">
        {RESULTS.map(([v, label, Icon, on]) => (
          <button key={v} type="button" disabled={readOnly} onClick={() => onChange({ ...item, result: item.result === v ? null : v })} data-testid={`check-item-${idx}-${v}`}
            className={`h-11 min-w-[76px] px-3 border flex items-center justify-center gap-1.5 text-sm font-semibold transition-colors ${item.result === v ? on : "border-[#334155] text-slate-300 hover:border-slate-400"} disabled:cursor-default`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
        {!readOnly && <button type="button" onClick={() => setShowComment(!showComment)} className="h-11 px-3 text-slate-400 hover:text-white" aria-label="Comment" data-testid={`check-item-${idx}-comment-toggle`}><MessageSquare className="w-4 h-4" /></button>}
        {!readOnly && item.result === "fail" && <button type="button" onClick={() => onPunch(item)} className="h-11 px-3 text-red-400 text-sm flex items-center gap-1 hover:text-red-300" data-testid={`check-item-${idx}-punch`}><Flag className="w-4 h-4" />Raise punch</button>}
      </div>
      {(showComment || (readOnly && item.comment)) && (
        <Input disabled={readOnly} value={item.comment} onChange={(e) => onChange({ ...item, comment: e.target.value })} placeholder="Comment" className="mt-2 bg-[#080D1A]" data-testid={`check-item-${idx}-comment`} />
      )}
    </div>
  );
}

export default function ChecksheetRunner() {
  const { cid } = useParams();
  const nav = useNavigate();
  const { me, can } = useAuth();
  const sn = useStageNames();
  const [s, setS] = useState(null);
  const [offline, setOffline] = useState(false);
  const [punchFor, setPunchFor] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => cachedGet(`/checksheets/${cid}`).then((r) => {
    const pending = queue.list().find((q) => q.id === `chk-${cid}`);
    setS(pending ? { ...r.data, ...pending.body, status: pending.body.complete ? "complete" : "in_progress" } : { ...r.data, signed_by: r.data.signed_by || me.name || "" });
    setOffline(r.offline);
  }).catch((e) => toast.error(errMsg(e)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [cid]);
  if (!s) return <Loading />;

  const readOnly = !can("Field") || s.status === "complete";
  const setItem = (i) => (it) => setS({ ...s, items: s.items.map((x, j) => (j === i ? it : x)) });

  const save = async (complete) => {
    const body = { items: s.items, photos: s.photos, signature: s.signature, signed_by: s.signed_by, complete, client_ts: new Date().toISOString() };
    setBusy(true);
    try {
      const r = await api.put(`/checksheets/${cid}`, body);
      toast.success(complete ? "Checksheet completed and signed" : "Progress saved");
      setS({ ...s, status: r.data.status, completed_at: r.data.completed_at, photos: r.data.photos, signature: r.data.signature });
      if (complete) nav(-1);
    } catch (e) {
      if (e.response) toast.error(errMsg(e));
      else {
        queue.add({ id: `chk-${cid}`, method: "put", url: `/checksheets/${cid}`, body, label: `${s.tag_no} ${s.template_code}` });
        setCache(`/checksheets/${cid}`, { ...s, ...body });
        toast.success("Saved on this device. It will sync when you reconnect.");
        if (complete) setS({ ...s, status: "complete" });
      }
    }
    setBusy(false);
  };

  const reopen = async () => {
    try { await api.post(`/checksheets/${cid}/reopen`); toast.success("Checksheet reopened"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const answered = s.items.filter((i) => i.result).length;
  return (
    <div className="max-w-3xl mx-auto" data-testid="checksheet-runner">
      <Link to={`/app/projects/${s.project_id}/checksheets`} className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white mb-4" data-testid="runner-back"><ArrowLeft className="w-4 h-4" />Checksheets</Link>
      <div className="panel p-5 mb-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="eyebrow">{s.template_code} · Stage {s.stage} · {sn[s.stage]}</p>
            <h1 className="num text-2xl md:text-3xl font-bold text-slate-50 mt-1" data-testid="runner-tag">{s.tag_no}</h1>
            <p className="text-slate-300">{s.template_name}</p>
            {s.tag && <p className="text-sm text-slate-500 mt-1">{s.tag.description} · {s.tag.equipment_type} · <span className="capitalize">{s.discipline}</span></p>}
          </div>
          <StatusPill status={s.status} />
        </div>
        {offline && <p className="text-xs text-amber-400 mt-3 font-mono">OFFLINE COPY · changes are stored on this device</p>}
        {s.status === "complete" && s.completed_at && <p className="text-xs text-slate-400 mt-3">Signed by {s.signed_by} on {fmtDateTime(s.completed_at)}</p>}
      </div>
      <div className="panel px-5 mb-4">
        <div className="flex justify-between items-center py-3 border-b border-[#1E2D4A] font-mono text-[11px] uppercase tracking-wider text-slate-500"><span>Check items</span><span data-testid="runner-answered">{answered} / {s.items.length}</span></div>
        {s.items.map((it, i) => <ItemRow key={i} idx={i} item={it} readOnly={readOnly} onChange={setItem(i)} onPunch={(item) => setPunchFor(item)} />)}
      </div>
      <div className="panel p-5 mb-4 space-y-3">
        <p className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Photo evidence</p>
        <PhotoStrip photos={s.photos || []} onChange={(photos) => setS({ ...s, photos })} disabled={readOnly} max={12} testid="runner-photo" />
      </div>
      <div className="panel p-5 mb-4 space-y-3">
        <p className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Signature</p>
        <Input disabled={readOnly} value={s.signed_by || ""} onChange={(e) => setS({ ...s, signed_by: e.target.value })} placeholder="Name" className="bg-[#080D1A]" data-testid="runner-signed-by" />
        <SignaturePad value={s.signature} onChange={(signature) => setS((x) => ({ ...x, signature }))} disabled={readOnly} />
      </div>
      {s.punch?.length > 0 && (
        <div className="panel p-5 mb-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-slate-500 mb-2">Punch on this tag</p>
          {s.punch.map((p) => <p key={p.id} className="text-sm text-slate-300 py-1"><span className="num text-amber-400">{p.number}</span> · Cat {p.category} · {p.description} · <StatusPill status={p.status} /></p>)}
        </div>
      )}
      <div className="sticky bottom-16 md:bottom-0 bg-[#080D1A]/95 backdrop-blur py-3 flex gap-2">
        {!readOnly && <>
          <Btn variant="ghost" onClick={() => save(false)} disabled={busy} className="flex-1 h-12" data-testid="runner-save">Save progress</Btn>
          <Btn onClick={() => save(true)} disabled={busy || answered < s.items.length || !s.signature || !s.signed_by} className="flex-1 h-12" data-testid="runner-complete">Complete and sign</Btn>
        </>}
        {s.status === "complete" && can("Manager") && <Btn variant="ghost" onClick={reopen} className="h-12" data-testid="runner-reopen">Reopen</Btn>}
      </div>
      <PunchDialog open={!!punchFor} setOpen={(o) => !o && setPunchFor(null)} projectId={s.project_id} tag={s.tag} defaultStage={s.stage}
        defaultDescription={punchFor ? `${s.template_code}: ${punchFor.text} failed${punchFor.comment ? ` - ${punchFor.comment}` : ""}` : ""} onSaved={() => load()} />
    </div>
  );
}
